import React, { useEffect } from 'react';
import { 
  GraduationCap, ShieldCheck, Lock, ArrowRight, UserPlus, 
  CheckCircle2, X, Sparkles, LogIn, Stethoscope
} from 'lucide-react';
import { useThemeTokens } from '../hooks/useActiveTheme';
import { StorageService } from '../services/storageService';

interface BenefitItem {
  title?: string;
  desc?: string;
}

interface AiFeatureAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  featureTitle?: string;
  featureDescription?: string;
  badgeLabel?: string;
  benefits?: Array<BenefitItem | string>;
  source?: string;
  dismissText?: string;
}

export const AiFeatureAuthModal: React.FC<AiFeatureAuthModalProps> = ({
  isOpen,
  onClose,
  featureTitle = "Faculty Clinical Mismatch Breakdown",
  featureDescription = "Get personalized faculty clinical audits explaining why your selected choice was incorrect and how nurse educators and board examiners evaluate patient safety.",
  badgeLabel = "Faculty Feature",
  benefits,
  source = "practice_quiz_explain_mismatch",
  dismissText = "Continue without signing up"
}) => {
  const tokens = useThemeTokens();

  const defaultBenefits = [
    { title: "Targeted Distractor Diagnosis:", desc: "Explains the precise clinical misconception in your selected answer." },
    { title: "Faculty Board Standards:", desc: "Master the clinical safety protocols taught by experienced nurse educators." },
    { title: "Mistake Vault Tracking:", desc: "Automatically saves missed questions for targeted review and mastery." }
  ];

  const activeBenefits = benefits && benefits.length > 0 ? benefits : defaultBenefits;

  useEffect(() => {
    if (isOpen) {
      StorageService.trackUserEvent({
        action: 'faculty_feature_auth_modal_shown',
        category: 'conversion',
        label: `Faculty Feature Auth Modal: ${featureTitle}`,
        metadata: {
          featureTitle,
          source,
          url: window.location.pathname
        }
      });
    }
  }, [isOpen, featureTitle, source]);

  if (!isOpen) return null;

  const handleSignUp = () => {
    StorageService.trackUserEvent({
      action: 'faculty_auth_signup_clicked',
      category: 'conversion',
      label: `Sign Up clicked from ${featureTitle}`,
      metadata: { featureTitle, source }
    });

    const currentUrl = window.location.href;
    const registerUrl = `https://www.diamondnursingschool.com/studyhub-login?mode=register&redirect=${encodeURIComponent(currentUrl)}`;
    window.location.href = registerUrl;
  };

  const handleLogin = () => {
    StorageService.trackUserEvent({
      action: 'faculty_auth_login_clicked',
      category: 'conversion',
      label: `Login clicked from ${featureTitle}`,
      metadata: { featureTitle, source }
    });

    const currentUrl = window.location.href;
    const loginUrl = `https://www.diamondnursingschool.com/studyhub-login?redirect=${encodeURIComponent(currentUrl)}`;
    window.location.href = loginUrl;
  };

  return (
    <div 
      id="ai-feature-auth-backdrop"
      className="fixed inset-0 z-[99999] bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto animate-fade-in text-left"
    >
      <div 
        id="ai-feature-auth-modal"
        className="relative w-full max-w-lg bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-200/80 dark:border-slate-800 shadow-2xl p-6 sm:p-8 space-y-6 my-auto animate-scale-up overflow-hidden"
      >
        {/* Ambient Top Glow */}
        <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-emerald-500 via-teal-500 to-indigo-500" />
        
        {/* Close Button */}
        <button 
          id="close-ai-auth-modal-btn"
          onClick={onClose}
          className="absolute top-5 right-5 p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
          aria-label="Close dialog"
        >
          <X size={18} />
        </button>

        {/* Header with Icon & Locked Badge */}
        <div className="flex items-start gap-4 pr-8">
          <div className={`p-4 rounded-2xl shadow-xl flex items-center justify-center shrink-0 ${tokens.primaryBtn}`}>
            <GraduationCap size={28} className="text-white" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 mb-1">
              <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[9px] font-black tracking-widest uppercase ${tokens.badge}`}>
                <Lock size={10} /> {badgeLabel}
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black tracking-widest uppercase bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-900/40">
                100% Free
              </span>
            </div>
            <h3 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight leading-tight">
              Unlock {featureTitle}
            </h3>
          </div>
        </div>

        {/* Description & Value Proposition */}
        <p className="text-slate-600 dark:text-slate-300 text-xs sm:text-sm font-semibold leading-relaxed">
          {featureDescription}
        </p>

        {/* High Conversion Value Checklist */}
        <div className="p-4 bg-slate-50 dark:bg-slate-850/60 rounded-2xl border border-slate-100 dark:border-slate-800 space-y-2.5">
          <div className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1">
            Free Account Benefits:
          </div>
          {activeBenefits.map((b, idx) => {
            const isStr = typeof b === 'string';
            const title = isStr ? '' : (b as BenefitItem).title;
            const desc = isStr ? (b as string) : (b as BenefitItem).desc;
            return (
              <div key={idx} className="flex items-start gap-2.5 text-xs font-bold text-slate-700 dark:text-slate-200">
                <CheckCircle2 size={16} className="text-emerald-500 shrink-0 mt-0.5" />
                <span>{title ? <strong>{title} </strong> : null}{desc}</span>
              </div>
            );
          })}
        </div>

        {/* Action Buttons */}
        <div className="space-y-3 pt-1">
          <button
            id="ai-modal-create-account-btn"
            onClick={handleSignUp}
            className={`w-full py-4 px-6 rounded-2xl font-black text-xs sm:text-sm uppercase tracking-wider text-white shadow-xl transition-all active:scale-98 flex items-center justify-center gap-3 cursor-pointer ${tokens.primaryBtn}`}
          >
            <UserPlus size={18} />
            <span>Create Free Account to Unlock</span>
            <ArrowRight size={16} />
          </button>

          <button
            id="ai-modal-signin-btn"
            onClick={handleLogin}
            className="w-full py-3.5 px-6 rounded-2xl font-black text-xs uppercase tracking-wider text-slate-700 dark:text-slate-200 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-750 transition-all active:scale-98 flex items-center justify-center gap-2 border border-slate-200 dark:border-slate-700 cursor-pointer"
          >
            <LogIn size={16} />
            <span>Already have an account? Sign In</span>
          </button>

          <div className="text-center pt-1">
            <button
              id="ai-modal-dismiss-btn"
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 text-xs font-bold transition-colors cursor-pointer"
            >
              {dismissText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
